// strategyGuards.ts — pure, side-effect-free strategy-graph guards.
//
// These exist because the engine has a sharp edge: a STOP_LOSS / TAKE_PROFIT
// block that has ANY indicator wired into it is treated as an *indicator
// gate* (close ALL positions whenever that input is truthy) and its own price
// config (stopType=atr_based / percentage / …, tpType=risk_reward / …) is
// SILENTLY ignored. A single stray "ATR > 0.001"-style expression therefore
// converts a 2×ATR stop into "exit every bar" — which is exactly how the
// 1m BTC deployment was bleeding (entry → forced stop-out on the next candle).
//
// This module makes that footgun loud: it is surfaced in the Builder
// validation panel AND it makes the live deployment refuse to run silently
// (the deployment-runner returns `unsupported`, visible in the health strip),
// so the system can never again quietly reinterpret a price stop as an
// unconditional truthy exit.

import type { Block } from '../types';
import { BlockType } from '../types';

export interface GuardIssue {
  blockId?: string;
  code: string;
  message: string;
  suggestion: string;
}

// Every stopType/tpType is a PRICE construct in the block schema — none of
// them mean "indicator". So if such a block also has a wired input, the price
// config is being discarded. We flag regardless of the specific type.
const PRICE_STOP_TYPES = new Set([
  'fixed_price', 'fixed_points', 'percentage', 'atr_based',
  'breakeven', 'trailing', 'trailing_limit', 'time_based', 'volatility_adjusted',
]);
const PRICE_TP_TYPES = new Set([
  'fixed_price', 'fixed_points', 'percentage', 'risk_reward',
  'atr_based', 'trailing', 'scaled',
]);

// Exit types the engine ACTUALLY resolves to a real price (verified against
// resolveStopLossPrice / resolveTakeProfitPrice + the run-loop). Anything
// outside these is NOT implemented:
//   • such a STOP_LOSS silently falls back to a fixed 1% stop (a different,
//     unconfigured stop — a silent reinterpretation), and
//   • such a TAKE_PROFIT yields NO target at all (an inert exit).
// We surface this loudly instead of letting an inert/mis-stop ship silently.
//
// Phase X.1+25 — atr_based STOP_LOSS is now engine-implemented (the
// resolver reads atr_based blocks with `atrMultiplier`, optional
// `anchor: 'swept_extreme'` + `sweepLookback`, and optional
// `tickBufferTicks`). Added to the implemented set so the guard stops
// flagging it as a silent mis-stop. The TAKE_PROFIT side still lacks
// atr_based support — that's still inert and stays out of TP_IMPLEMENTED.
const SL_IMPLEMENTED = new Set([
  'fixed_price', 'fixed_points', 'percentage', 'trailing', 'trailing_limit',
  'atr_based',
]);
const TP_IMPLEMENTED = new Set([
  'fixed_price', 'fixed_points', 'percentage', 'risk_reward',
]);

export function detectUnimplementedExits(blocks: Block[]): GuardIssue[] {
  const issues: GuardIssue[] = [];
  for (const b of blocks ?? []) {
    const data = (b?.data ?? {}) as Record<string, unknown>;
    const label = String(data.name ?? b.id);
    if (b.type === BlockType.STOP_LOSS) {
      const st = String(data.stopType ?? 'fixed_points');
      if (!SL_IMPLEMENTED.has(st)) {
        issues.push({
          blockId: b.id,
          code: 'GUARD_UNIMPL_SL',
          message:
            `Stop Loss "${label}" uses stopType="${st}", which the engine does ` +
            `NOT implement as a price stop. It SILENTLY falls back to a fixed ` +
            `1% stop — NOT the ${st} stop you configured. This is a silent ` +
            `mis-stop, not real risk management.`,
          suggestion:
            `Use an implemented stop type: percentage, fixed_points, ` +
            `fixed_price, or trailing.`,
        });
      }
    } else if (b.type === BlockType.TAKE_PROFIT) {
      const tt = String(data.tpType ?? 'risk_reward');
      if (!TP_IMPLEMENTED.has(tt)) {
        issues.push({
          blockId: b.id,
          code: 'GUARD_UNIMPL_TP',
          message:
            `Take Profit "${label}" uses tpType="${tt}", which the engine does ` +
            `NOT implement — NO take-profit is applied at all. This is an ` +
            `INERT exit: the position has no profit target.`,
          suggestion:
            `Use an implemented target type: risk_reward, percentage, ` +
            `fixed_points, or fixed_price.`,
        });
      }
    }
  }
  return issues;
}

// FOOTGUN: price-configured SL/TP block that ALSO has an indicator wired in.
export function detectWiredPriceExits(blocks: Block[]): GuardIssue[] {
  const issues: GuardIssue[] = [];
  for (const b of blocks ?? []) {
    const wired = (b?.connections?.inputs?.length ?? 0) > 0;
    if (!wired) continue;
    const data = (b?.data ?? {}) as Record<string, unknown>;
    const label = String(data.name ?? b.id);

    if (b.type === BlockType.STOP_LOSS) {
      const st = String(data.stopType ?? 'fixed_points');
      if (PRICE_STOP_TYPES.has(st)) {
        issues.push({
          blockId: b.id,
          code: 'GUARD_WIRED_PRICE_SL',
          message:
            `Stop Loss "${label}" is a PRICE stop (stopType="${st}") but also has an ` +
            `indicator wired into it. The engine ignores the price stop entirely and ` +
            `instead closes ALL positions whenever that indicator is truthy — e.g. a ` +
            `"ATR > 0.001" expression makes it exit on essentially every bar.`,
          suggestion:
            `Unwire the indicator so this block uses its ${st} price stop, OR use a ` +
            `dedicated indicator Exit block and remove the price config here.`,
        });
      }
    } else if (b.type === BlockType.TAKE_PROFIT) {
      const tt = String(data.tpType ?? 'risk_reward');
      if (PRICE_TP_TYPES.has(tt)) {
        issues.push({
          blockId: b.id,
          code: 'GUARD_WIRED_PRICE_TP',
          message:
            `Take Profit "${label}" is a PRICE target (tpType="${tt}") but also has an ` +
            `indicator wired into it. The engine ignores the target and instead closes ` +
            `ALL positions whenever that indicator is truthy.`,
          suggestion:
            `Unwire the indicator so this block uses its ${tt} target, OR use a ` +
            `dedicated indicator Exit block and remove the price config here.`,
        });
      }
    }
  }
  return issues;
}

// Sizing-config sanity. Catches the class of bug where the method can't be
// resolved or the magnitude is missing/zero — which now lands on the SAFE
// %-equity default rather than the old unsafe raw-units fallback, but the
// user should still be told their config isn't being read as intended.
export function validateSizingConfig(blocks: Block[]): GuardIssue[] {
  const cfg = (blocks ?? []).find(b => b.type === BlockType.STRATEGY_CONFIG)
    ?.data as Record<string, unknown> | undefined;
  if (!cfg) return [];
  const issues: GuardIssue[] = [];
  const method = cfg.default_qty_type ?? cfg.position_sizing_method;
  const value = Number(cfg.default_qty_value);
  if (method == null || String(method).trim() === '') {
    issues.push({
      code: 'GUARD_SIZING_NO_METHOD',
      message:
        'Strategy Config has no position-sizing method (default_qty_type). ' +
        'Sizing will use a conservative %-equity default instead of your intended method.',
      suggestion: 'Set Quantity Type (e.g. "% of equity") in the Strategy Configuration block.',
    });
  }
  if (!Number.isFinite(value) || value <= 0) {
    issues.push({
      code: 'GUARD_SIZING_NO_VALUE',
      message: 'Strategy Config has no positive position-size value (default_qty_value).',
      suggestion: 'Set a positive Quantity Value in the Strategy Configuration block.',
    });
  }
  return issues;
}
